import React from 'react';
import { LEGAL_CONTENT, ICONS } from './constants';
import type { LegalDoc } from '@types';
import { sanitizeHtml } from '../utils/safeMarkdown';

interface LegalPageProps {
    doc: LegalDoc;
    onNavigateHome: () => void;
    backLabel?: string;
}

const LegalPage: React.FC<LegalPageProps> = ({ doc, onNavigateHome, backLabel = "Back to Home" }) => {
    const { title, content } = LEGAL_CONTENT[doc];

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans selection:bg-blue-500 selection:text-white">
            
            {/* --- Premium Minimal Navigation --- */}
            <nav className="w-full z-50 top-0 left-0 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-3 cursor-pointer" onClick={onNavigateHome}>
                            <div className="h-7 w-7 text-blue-600">
                                {ICONS.logo && React.isValidElement(ICONS.logo) ? 
                                    React.cloneElement(ICONS.logo as React.ReactElement<any>, { className: 'h-7 w-7' }) : 
                                    <span className="font-bold text-xl">IO</span>
                                }
                            </div>
                            <div className="flex flex-col">
                                <span className="font-bold text-sm tracking-wider font-mono text-slate-900 uppercase leading-none">INTELOPTICS</span>
                                <span className="text-[9px] text-blue-600 font-bold tracking-[0.2em] uppercase mt-0.5">INSIGHTS</span>
                            </div>
                        </div>

                        <button
                            onClick={onNavigateHome}
                            className="group flex items-center px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-blue-600 transition-all border border-slate-200 rounded-lg bg-slate-50 hover:bg-white hover:border-blue-200"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            {backLabel}
                        </button>
                    </div>
                </div>
            </nav>

            {/* --- Policy Content --- */}
            <main className="flex-1 py-16 md:py-24 px-6 md:px-8">
                <div className="max-w-4xl mx-auto">
                    
                    {/* Page Header */}
                    <div className="mb-12 border-b border-slate-200 pb-12">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-[9px] font-black uppercase tracking-widest mb-6">
                            Operational Governance Framework
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-none mb-6">
                            {title}
                        </h1>
                        <p className="text-slate-500 text-sm font-medium">
                            Last Revised: April 2026 • Reference: SEC-REQ-VAL-V2
                        </p>
                    </div>

                    {/* Content Block */}
                    <div 
                        className="bg-white p-8 md:p-12 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 prose prose-slate prose-lg max-w-none 
                        prose-headings:text-slate-900 prose-headings:font-black prose-headings:tracking-tight
                        prose-p:text-slate-600 prose-p:leading-relaxed prose-p:font-medium
                        prose-li:text-slate-600 prose-li:font-medium
                        prose-strong:text-slate-900 prose-strong:font-bold
                        prose-a:text-blue-600 prose-a:font-bold prose-a:no-underline hover:prose-a:underline"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
                    />

                    {/* Contact Link */}
                    <div className="mt-12 p-8 bg-slate-900 rounded-2xl text-white flex flex-col md:flex-row justify-between items-center gap-6">
                        <div>
                            <h4 className="font-bold text-lg">Further Clarification Required?</h4>
                            <p className="text-slate-400 text-sm">Contact our global governance and legal team for specific mission waivers.</p>
                        </div>
                        <a 
                            href="mailto:contact@eprims.com"
                            className="px-6 py-3 bg-white text-slate-900 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-blue-50 transition-colors"
                        >
                            Contact Compliance
                        </a>
                    </div>
                </div>
            </main>

            {/* --- Minimal Legal Footer --- */}
            <footer className="py-12 border-t border-slate-200 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] mb-4">
                        &copy; 2026 Eprims Ltd and IntelOptics Insights. All Rights Reserved.
                    </p>
                    <div className="flex justify-center gap-4 text-[9px] font-black uppercase tracking-widest text-slate-500 opacity-60">
                        <span>ISO 27001 Certified</span>
                        <span>•</span>
                        <span>GDPR Article 28 Ready</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LegalPage;
